<script lang="ts">
  import { onMount } from 'svelte';
  import * as apiService from '../lib/apiService';
  import { showToast } from '../lib/toast';
  import { _ } from 'svelte-i18n';
  import type { TeamMember, Team, Club } from '../types';
  import { generateUUID, checkOnboardingStep } from '../lib/utils';
  import ConfirmationModal from '../components/ConfirmationModal.svelte';
  import OnboardingModal from '../components/OnboardingModal.svelte';
  import ClubCard from '../components/ClubCard.svelte';
  import TeamForm from '../components/TeamForm.svelte';
  import EmailChangeModal from '../components/EmailChangeModal.svelte';
  import MigrationBanner from '../components/MigrationBanner.svelte';
  import { settingsStore, refreshSettings } from '../stores/settings';
  import { actionFlags, actionFlagHelpers } from '../stores/actionFlags';
  import type { NotificationPreference } from '../types';

  // Tabs
  type Tab =  | 'teams' | 'notifications' | 'settings';
  let activeTab: Tab = 'teams';
  let addTeam = false;
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
    
  // Account state
  let showDeleteAccountConfirmation = false;
  
  // Welcome modal state
  let showOnboardingModal = false;

  // Club state
  let selectedTeam: Team | null = null;
  $: clubsWithTeams = $settingsStore.teams?.reduce((acc, team) => {
    if (team.club) {
      const clubId = team.club.id;
      if (!acc[clubId]) {
        acc[clubId] = {
          club: team.club,
          teams: []
        };
      }
      acc[clubId].teams.push(team);
    }
    return acc;
  }, {} as Record<string, { club: Club, teams: Team[] }>) || {};

  // Teams state
  let showRemoveConfirmation = false;
  let teamToRemove: Team | null = null;
  
  // Leave team state  
  let showLeaveTeamConfirmation = false;
  let teamToLeave: Team | null = null;
  
  // Notification preferences state
  let notificationPreferences: NotificationPreference[] = [];

  // Email management state
  let showEmailChangeModal = false;
  $: userHasEmail = $settingsStore.user?.email && $settingsStore.user.email.trim() !== '';
  $: showMigrationBanner = !userHasEmail;
  

  // Fetch current settings on mount
  onMount(async () => {
    try {
      // Wait for settings to be loaded - if they're already loading in App.svelte, this will wait for that to complete
      await refreshSettings();
            
      // Parse tab and view from URL - handle both regular query params and hash-based params
      let tabParam, viewParam;
      
      // First check normal query params
      const urlParams = new URLSearchParams(window.location.search);
      tabParam = urlParams.get('tab');
      viewParam = urlParams.get('view');
      
      // If not found, check if it's after the hash
      if ((!tabParam || !viewParam) && window.location.hash) {
        const hashParts = window.location.hash.split('?');
        if (hashParts.length > 1) {
          const hashParams = new URLSearchParams(hashParts[1]);
          if (!tabParam) tabParam = hashParams.get('tab');
          if (!viewParam) viewParam = hashParams.get('view');
        }
      }
            
      // Handle tab parameter
      if (['teams', 'settings', 'notifications'].includes(tabParam)) {
        setActiveTab(tabParam as Tab);
        apiService.trackEvent('select_settings_tab', {
            module: "settings",
            action_type: "select_view",
            description: 'Select settings tab',
            value: tabParam
        }, $settingsStore.user.id);
      }

      // Handle view parameter
      if (tabParam === 'teams' && viewParam === 'add') {
        addTeam = true; // Open the add team form
      }

      
    } catch (error) {
      console.error('Error loading settings:', error);
    }

  });
    
  //---------- ACCOUNT SETTINGS ----------

  // Handle field changes with debouncing
  function handleUserFieldChange() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => saveUserSettings(), 1000);
  }
  
  // Save settings
  async function saveUserSettings() {
    // Prevent overlapping updates
    if ($actionFlags.isUpdatingUserSettings) {
      console.log('User settings update already in progress, skipping...');
      return;
    }

    actionFlagHelpers.setFlag('isUpdatingUserSettings');
    try {
      // Save to backend
      await apiService.updateUserdata({username: $settingsStore.user.username}, (result, status) => {
        if (status === 200) {
          $settingsStore.user = result;
          apiService.trackEvent('update_settings', {
              module: "settings",
              action_type: "update_data",
              description: 'Update settings'
          }, $settingsStore.user.id);
        }
      });
      
      // showToast($_('settings.common.saveSuccess'));
    } catch (error) {
      console.error('Error saving user settings:', error);
      showToast($_('settings.common.saveError'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isUpdatingUserSettings');
    }
  }

  // Show delete account confirmation
  function showDeleteAccount() {
    showDeleteAccountConfirmation = true;
  }

  // Cancel delete account
  function cancelDeleteAccount() {
    showDeleteAccountConfirmation = false;
  }

  // Confirm and delete account
  async function confirmDeleteAccount() {
    actionFlagHelpers.setFlag('isDeletingAccount');

    try {
      await apiService.deleteAccount((result, status) => {
        actionFlagHelpers.clearFlag('isDeletingAccount');
        
        if (status === 200) {
          showToast($_('settings.user.delete_account_success'), 'success');
          // Clear local storage and redirect to home page after successful deletion
          localStorage.removeItem('token');
          apiService.trackEvent('delete_account', {
            module: "settings",
            action_type: "account_changes",
            description: 'Delete account',
          }, $settingsStore.user.id);
          setTimeout(() => {
            window.location.href = '/account/#/login';
          }, 2000);
        } else {
          showToast(result || $_('settings.user.delete_account_error'), 'error');
          cancelDeleteAccount();
        }
      });
    } catch (error) {
      actionFlagHelpers.clearFlag('isDeletingAccount');
      showToast($_('settings.user.delete_account_error'), 'error');
      console.error('Error deleting account:', error);
      cancelDeleteAccount();
    }
  }

  //---------- EMAIL MANAGEMENT ----------

  function openEmailChangeModal() {
    showEmailChangeModal = true;
  }

  function closeEmailChangeModal(event: CustomEvent) {
    showEmailChangeModal = false;
    // If email was successfully updated, refresh settings
    if (event.detail?.email) {
      refreshSettings();
      showToast($_('settings.email.change_success'), 'success');
    }
  }

  async function handleSendEmailCode(event: CustomEvent) {
    const { email, onSuccess, onError } = event.detail;

    try {
      await apiService.initiateEmailChange(email, (result, status) => {
        if (status === 200) {
          onSuccess?.();
        } else {
          onError?.(result || $_('settings.email.send_error'));
        }
      });
    } catch (error) {
      onError?.($_('settings.email.send_error'));
    }
  }

  async function handleVerifyEmailCode(event: CustomEvent) {
    const { code, onSuccess, onError } = event.detail;

    try {
      await apiService.confirmEmailChange(code, (result, status) => {
        if (status === 200) {
          onSuccess?.(result.email);
        } else {
          onError?.(result || $_('settings.email.verify_error'));
        }
      });
    } catch (error) {
      onError?.($_('settings.email.verify_error'));
    }
  }

  //---------- TEAMS ----------

  function handleAddTeam() {
    selectedTeam = {
      id: '',
      name: '',
      club_id: '',
      default_meeting_location: '',
      team_members: []
    } as Team;
  }

  async function handleTeamCreate(team: Team) {
    try {
    // Create new team
        await apiService.createTeam({
          club_id: team.club_id,
          team_name: team.name
        }, async (result, status) => {
          if (status == 200 || status == 201) {
            showToast($_('settings.addTeam.success'), 'success');
            await refreshSettings();
            handleTeamCancel();
          } else {
            showToast($_('settings.addTeam.error'), 'error');
          }
        });
    } catch(error) {
        showToast($_('settings.addTeam.error'), 'error');
    } finally {
      actionFlagHelpers.clearFlag("isCreatingTeam")
    }
  }

  async function handleDeleteTeam(team: Team) {
    showRemoveConfirmation = true;
    teamToRemove = team;
  }


  // Update or delete team
  async function handleTeamUpdate(team: Team, deleteTeam : boolean = false) {

    let event_name = 'update_team';
    let event_type = 'update_data';
    let event_description = 'Update team';

    try {
      if (deleteTeam) {
        event_name = 'delete_team';
        event_type = 'delete_data';
        event_description = 'Delete team';
        await apiService.deleteTeam(team.id, async (result, status) => {
          if (status === 400) {
            if (typeof result === 'string') {
              throw new Error(result);
            } else {
              throw new Error($_('settings.removeTeam.error'));
            }
          } else if (status === 403) {
            throw new Error($_('settings.removeTeam.unauthorized'));
          } else if (status !== 200 && status !== 201) {
            throw new Error($_('settings.removeTeam.error'));
          }
        });
      } else if (team?.id) {
        await apiService.updateTeam(team, (result, status) => {
          if (status === 400) {
            if (typeof result === 'string') {
              throw new Error(result);
            } else {
              throw new Error($_('settings.updateTeam.error'));
            }
          } else if (status === 403) {
            throw new Error($_('settings.updateTeam.unauthorized'));
          } else if (status !== 200 && status !== 201) {
            throw new Error($_('settings.updateTeam.error'));
          }
        });
      }
    } catch (error) {
      showToast(error?.message || $_('settings.updateTeam.error'), 'error');
      console.error('Error updating team:', error);
    } finally {
      await refreshSettings();
      if (selectedTeam && selectedTeam.id === team.id) {
        if (deleteTeam) {
          selectedTeam = null;
        } else {
          selectedTeam = {...team};
        }
      }
      // Refresh settings to get updated teams list
      apiService.trackEvent(event_name, {
        module: "settings",
        action_type: event_type,
        description: event_description,
      }, $settingsStore.user.id);
    }
  }

  function handleTeamCancel() {
    addTeam = false;
    selectedTeam = null;
  }

  // Remove team confirmation handlers
  async function confirmRemoveTeam() {
    if (teamToRemove) {
      await handleTeamUpdate(teamToRemove, true);
      showRemoveConfirmation = false;
      teamToRemove = null;
    }
  }

  function cancelRemoveTeam() {
    showRemoveConfirmation = false;
    teamToRemove = null;
  }

  // Leave team functionality
  function handleLeaveTeam(team: Team) {
    teamToLeave = team;
    showLeaveTeamConfirmation = true;
  }

  async function confirmLeaveTeam() {
    if (!teamToLeave) return;
    
    try {
      await apiService.removeTeamUser(teamToLeave.id, $settingsStore.user.id, (result, status) => {
        if (status === 200) {
          showToast($_('settings.leaveTeam.success'), 'success');
          // Refresh settings to get updated teams list
          refreshSettings();
        } else {
          showToast(result?.message || $_('settings.leaveTeam.error'), 'error');
        }
      });
    } catch (error) {
      console.error('Error leaving team:', error);
      showToast($_('settings.leaveTeam.error'), 'error');
    } finally {
      showLeaveTeamConfirmation = false;
      teamToLeave = null;
    }
  }

  function cancelLeaveTeam() {
    showLeaveTeamConfirmation = false;
    teamToLeave = null;
  }


  //---------- TEAM MEMBERS ----------


  // Add a new team member
  async function handleAddTeamMember(event: CustomEvent) {
    const { team, name, onSuccess, onError } = event.detail;

    if ($actionFlags.isAddingTeamMember) return;

    actionFlagHelpers.setFlag("isAddingTeamMember")
    console.log('Adding team member in Settings:', team, name);
    
    try {
      if (!name) {
        throw new Error($_('settings.teamMembers.errors.enterName'));
      }
      // Check for duplicates (case insensitive)
      if (team.team_members.some(member => 
        member.name.toLowerCase() === name.toLowerCase())) {
        throw new Error($_('settings.teamMembers.errors.alreadyExists'));
      }

      const newMember: TeamMember = {
        id: generateUUID(),
        name: name
      };
      team.team_members.push(newMember);
      apiService.trackEvent('add_team_member', {
          module: "settings",
          action_type: "update_settings",
          description: 'Add team member',
        }, $settingsStore.user.id);
      await handleTeamUpdate(team);
      onSuccess?.(); // Call success callback
    } catch (error) {
      console.error('Error adding team member:', error);
      showToast(error?.message || $_('settings.teamMembers.errors.error'), 'error');
      onError?.(error); // Call error callback
    } finally {
      actionFlagHelpers.clearFlag('isAddingTeamMember');
    }
  }


  // Remove a team member
  async function handleRemoveTeamMember(event: CustomEvent) {
    const { team, index, onSuccess, onError } = event.detail;

    if ($actionFlags.isRemovingTeamMember) return;

    actionFlagHelpers.setFlag("isRemovingTeamMember")
    console.log('Removing team member in Settings:', team, index);

  
    try {
      const memberToRemove = team.team_members[index];
      const updatedTeam = {
        ...team,
        team_members: team.team_members.filter((_, i) => i !== index)
      };
      apiService.trackEvent('delete_team_member', {
        module: "settings",
        action_type: "update_settings",
        description: 'Delete team member',
      }, $settingsStore.user.id);
      await handleTeamUpdate(updatedTeam);
      showToast($_('settings.teamMembers.success.removed', { values: { name: memberToRemove.name } }));
      onSuccess?.(); // Call success callback
    } catch (error) {
      console.error('Error removing team member:', error);
      showToast('Failed to remove team member', 'error');
      onError?.(error); // Call error callback
    } finally {
      actionFlagHelpers.clearFlag('isRemovingTeamMember');
    }
  }


  //---------- ONBOARDING ----------

  // Check if user needs account settings onboarding
  async function checkAccountSettingsOnboarding() {
    try {
      const hasSeenOnboarding = await checkOnboardingStep('account_settings');
      if (!hasSeenOnboarding) {
        // Show welcome modal after a short delay for better UX
        setTimeout(() => {
          showOnboardingModal = true;
        }, 500);
      }
    } catch (error) {
      console.error('Error checking account settings onboarding:', error);
    }
  }

  // Close welcome modal
  function closeOnboardingModal() {
    showOnboardingModal = false;
  }

  //---------- NOTIFICATIONS ----------

  // Load notification preferences
  async function loadNotificationPreferences() {
    if ($actionFlags.isLoadingPreferences) return;
    
    try {
      actionFlagHelpers.setFlag('isLoadingPreferences');
      await apiService.getNotificationPreferences((result, status) => {
        actionFlagHelpers.clearFlag('isLoadingPreferences');
        if (status === 200) {
          notificationPreferences = result.preferences || [];
          // Update settings store
          $settingsStore.notification_preferences = notificationPreferences;
        } else {
          console.error('Error loading notification preferences:', result);
          showToast($_('settings.notifications.load_error'), 'error');
        }
      });
    } catch (error) {
      actionFlagHelpers.clearFlag('isLoadingPreferences');
      console.error('Error loading notification preferences:', error);
      showToast($_('settings.notifications.load_error'), 'error');
    }
  }

  // Update notification preference
  async function updatePreference(notificationType: 'match_announcement' | 'match_start' | 'match_report_reminder', enabled: boolean) {
    if ($actionFlags.isUpdatingNotificationPreference) return;

    actionFlagHelpers.setFlag('isUpdatingNotificationPreference');
    try {
      
      await apiService.updateNotificationPreference(notificationType, enabled, (result, status) => {        
        if (status === 200) {
          // Update local state
          const prefIndex = notificationPreferences.findIndex(p => p.notification_type === notificationType);
          if (prefIndex !== -1) {
            notificationPreferences[prefIndex] = result.preference;
          } else {
            notificationPreferences = [...notificationPreferences, result.preference];
          }
          
          // Update settings store
          $settingsStore.notification_preferences = notificationPreferences;
          
          apiService.trackEvent('update_notifications', {
              module: "settings",
              action_type: "update_data",
              description: 'Update notifications',
              value: notificationType
          }, $settingsStore.user.id);
        } else {
          throw new Error();
        }
      });
    } catch (error) {
      showToast(error?.message || $_('settings.notifications.update_error'), 'error');
    } finally {
      actionFlagHelpers.clearFlag('isUpdatingNotificationPreference');
    }
  }

  // Get preference enabled state
  function isPreferenceEnabled(notificationType: string): boolean {
    const pref = notificationPreferences.find(p => p.notification_type === notificationType);
    return pref ? pref.enabled : true; // Default to enabled if not found
  }

  // Reactive preferences for binding
  $: matchAnnouncementEnabled = notificationPreferences && isPreferenceEnabled('match_announcement');
  $: matchStartEnabled = notificationPreferences && isPreferenceEnabled('match_start');
  $: matchReportEnabled = notificationPreferences && isPreferenceEnabled('match_report_reminder');

  //---------- TABS ----------

  // Change active tab
  function setActiveTab(tab: Tab) {
    activeTab = tab;
    
    // Check for onboarding 
    checkAccountSettingsOnboarding();
    
    // Load notification preferences when switching to notifications tab
    if (tab === 'notifications') {
      loadNotificationPreferences();
    }
    
    // Update URL without reload, preserving the hash routing
    if (window.location.hash.includes('?')) {
      // If URL already has query params after hash
      const basePath = window.location.hash.split('?')[0];
      window.history.replaceState({}, '', `${basePath}?tab=${tab}`);
    } else {
      // Otherwise add query params to existing hash
      window.history.replaceState({}, '', `${window.location.hash}?tab=${tab}`);
    }
  }
  
    
</script>

<!-- Migration Banner for users without email -->
<MigrationBanner show={showMigrationBanner} showButton={activeTab !== 'settings'} on:goToSettings={() => setActiveTab('settings')} />

<section class="mt-6">
    <a class="secondary outline full-width" role="button" href="/game-assist/">{$_('settings.match_manager')} <ion-icon name="arrow-forward-circle"></ion-icon></a>
</section>

<!-- Tab Navigation -->
<section class="mt-6">
  <nav class="tabs">
    <ul>
      <li>
        <a role="button" href="?tab=teams" class="secondary" class:active={activeTab === 'teams'} on:click|preventDefault={() => setActiveTab('teams')}>
          {$_('settings.tabs.teams')}
        </a>
      </li>
      <li>
        <a role="button" href="?tab=notifications" class="secondary" class:active={activeTab === 'notifications'} on:click|preventDefault={() => setActiveTab('notifications')}>
          {$_('settings.tabs.notifications')}
        </a>
      </li>
      <li>
        <a role="button" href="?tab=settings" class="secondary" class:active={activeTab === 'settings'} on:click|preventDefault={() => setActiveTab('settings')}>
          {$_('settings.tabs.settings')}
        </a>
      </li>
    </ul>
  </nav>
</section>

<!-- Settings Tab -->
{#if activeTab === 'settings'}
  <section class="mt-6">

    <article>
    <!-- Email field -->
    <div class="email-setting">
      <label for="email">{$_('settings.user.email')}</label>
      <div class="email-display">
        {#if userHasEmail}
          <span class="email-value">{$settingsStore.user.email}</span>
          <button type="button" class="secondary small" on:click={openEmailChangeModal}>
            {$_('settings.email.change_button')}
          </button>
        {:else}
          <span class="email-placeholder">{$_('settings.email.not_set')}</span>
          <button type="button" class="primary small" on:click={openEmailChangeModal}>
            {$_('settings.email.add_button')}
          </button>
        {/if}
      </div>
    </div>

    <hr>

    <!-- Username field -->
    <label for="username">
      {$_('settings.user.username')}
      <input
        type="text"
        id="nickname"
        data-1p-ignore="true"
        bind:value={$settingsStore.user.username}
        on:input={handleUserFieldChange}
        placeholder={$_('settings.user.username_placeholder')}
      />
    </label>


    </article>

    <article>
      <h4>{$_('settings.user.delete_account')}</h4>
      <p>{$_('settings.user.delete_account_description')}</p>
        <button class="danger full-width" on:click={showDeleteAccount}>
        {$_('settings.user.delete_account_button')}
      </button>
    </article>
    
  </section>
{/if}

<!-- Teams Tab -->
{#if activeTab === 'teams'}
  <section class="mt-6">
    <p class="text text-italic text-muted text-sm">{$_('settings.teams.description')}</p>
    {#if !selectedTeam}
      <!-- Club Cards -->
      <div class="clubs-grid">
        {#each Object.values(clubsWithTeams) as clubData}
          <ClubCard 
            club={clubData.club}
            teams={clubData.teams}
            bind:selectedTeam
            onLeaveTeam={handleLeaveTeam}
          />
        {/each}
      </div>
    {:else}
      <!-- Team Form -->
      <TeamForm 
        team={selectedTeam}
        on:create={(e) => handleTeamCreate(e.detail)}
        on:update={(e) => handleTeamUpdate(e.detail)}
        on:delete={(e) => handleDeleteTeam(e.detail)}
        on:addTeamMember={handleAddTeamMember}
        on:removeTeamMember={handleRemoveTeamMember}
        on:cancel={handleTeamCancel}
      />
    {/if}
    <!-- Add New Team Button -->
    <button class="outline full-width mt-6" on:click={handleAddTeam}>
      <ion-icon name="add-circle"></ion-icon> {$_('settings.addTeam.button')}
    </button>
  </section>
{/if}

<!-- Notifications Tab -->
{#if activeTab === 'notifications'}
  <section class="mt-6">

    <p class="text">{$_('settings.notifications.description')}</p>

    {#if $actionFlags.isLoadingPreferences}
      <div class="loading-state">
        <p>{$_('settings.notifications.loading')}</p>
      </div>
    {:else}
      <hr>
      
      <!-- Match Announcement Notification -->
      <div class="notification-item">
        <div class="notification-content">
          <h5>📣 {$_('settings.notifications.notification.announcement_title')}</h5>
          <p>{$_('settings.notifications.notification.announcement_description')}</p>
        </div>
        <div class="notification-toggle">
          <label class="switch">
            <input 
              type="checkbox" 
              checked={matchAnnouncementEnabled}
              on:change={(e) => updatePreference('match_announcement', e.currentTarget.checked)}
              disabled={$actionFlags['updating_match_announcement']}
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>

      <!-- Match Start Notification -->
      <div class="notification-item">
        <div class="notification-content">
          <h5>🔔 {$_('settings.notifications.notification.start_match_title')}</h5>
          <p>{$_('settings.notifications.notification.start_match_description')}</p>
        </div>
        <div class="notification-toggle">
          <label class="switch">
            <input 
              type="checkbox" 
              checked={matchStartEnabled}
              on:change={(e) => updatePreference('match_start', e.currentTarget.checked)}
              disabled={$actionFlags['updating_match_start']}
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>

      <!-- Match Report Notification -->
      <div class="notification-item">
        <div class="notification-content">
          <h5>📋 {$_('settings.notifications.notification.report_title')}</h5>
          <p>{$_('settings.notifications.notification.report_description')}</p>
        </div>
        <div class="notification-toggle">
          <label class="switch">
            <input 
              type="checkbox" 
              checked={matchReportEnabled}
              on:change={(e) => updatePreference('match_report_reminder', e.currentTarget.checked)}
              disabled={$actionFlags['updating_match_report_reminder']}
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>
    {/if}

  </section>
{/if}

<!-- Remove Team Confirmation Modal -->
<ConfirmationModal
  isOpen={showRemoveConfirmation}
  title={$_('settings.removeTeam.title')}
  message={$_('settings.removeTeam.message')}
  confirmLabel={$_('common.confirm')}
  doubleConfirm={true}
  onConfirm={confirmRemoveTeam}
  onCancel={cancelRemoveTeam}
/>

<!-- Leave Team Confirmation Modal -->
<ConfirmationModal
  isOpen={showLeaveTeamConfirmation}
  title={$_('settings.leaveTeam.title')}
  message={$_('settings.leaveTeam.message', { values: { teamName: teamToLeave?.name } })}
  confirmLabel={$_('common.confirm')}
  doubleConfirm={true}
  onConfirm={confirmLeaveTeam}
  onCancel={cancelLeaveTeam}
/>

<!-- Delete Account Confirmation Modal -->
<ConfirmationModal
  isOpen={showDeleteAccountConfirmation}
  title={$_('settings.user.delete_account')}
  message={$_('settings.user.delete_account_confirmation')}
  confirmLabel={$_('common.confirm')}
  doubleConfirm={true}
  onConfirm={confirmDeleteAccount}
  onCancel={cancelDeleteAccount}
/>

<!-- Onboarding Modal -->
<OnboardingModal
  isOpen={showOnboardingModal}
  title={$_('onboarding.account_settings.title')}
  onClose={closeOnboardingModal}
>
  <p>{$_('onboarding.account_settings.intro')}</p>
  <ul>
    <li>{$_('onboarding.account_settings.step1')}</li>
    <li>{$_('onboarding.account_settings.step2')}</li>
    <li>{$_('onboarding.account_settings.step3')}</li>
  </ul>
</OnboardingModal>

<!-- Email Change Modal -->
<EmailChangeModal
  isOpen={showEmailChangeModal}
  currentEmail={$settingsStore.user?.email || ''}
  on:sendCode={handleSendEmailCode}
  on:verifyCode={handleVerifyEmailCode}
  on:close={closeEmailChangeModal}
/>

<style>
  .email-setting {
    margin-bottom: 1rem;
  }

  .email-display {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .email-value {
    font-weight: 500;
  }

  .email-placeholder {
    color: var(--pico-muted-color);
    font-style: italic;
  }

  .clubs-grid {
    display: grid;
    gap: 1rem;
    margin-bottom: 2rem;
  }
  
  @media (min-width: 768px) {
    .clubs-grid {
      grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    }
  }


  /* Notification preferences styles */
  .notification-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem 0;
    border-bottom: 1px solid var(--pico-border-color);
  }

  .notification-item:last-child {
    border-bottom: none;
  }

  .notification-content {
    flex: 1;
    margin-right: 1rem;
  }

  .notification-content h5 {
    margin-bottom: 0.5rem;
  }

  .notification-content p {
    margin: 0;
    color: var(--pico-muted-color);
  }

  .notification-toggle {
    flex-shrink: 0;
  }

  /* Toggle Switch Styles */
  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--pico-muted-border-color);
    transition: .4s;
    border-radius: 34px;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }

  input:checked + .slider {
    background-color: var(--pico-primary);
  }

  input:focus + .slider {
    box-shadow: 0 0 1px var(--pico-primary);
  }

  input:checked + .slider:before {
    transform: translateX(26px);
  }

  input:disabled + .slider {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .loading-state {
    text-align: center;
    padding: 2rem;
    color: var(--pico-muted-color);
  }
</style> 